import { useState } from 'react';
import {
  useReportShares,
  useMintShare,
  useRevokeShare,
  absoluteViewerUrl,
  type ShareMode,
  type ShareAudience,
  type MintShareResult,
  type ReportShare,
} from '../../api/shares.js';

/**
 * SharePanel — modal for minting and managing share links on a Report.
 *
 * Opened from the Report detail header. Shows existing shares + a
 * compact form for minting a new one. The plaintext token is displayed
 * exactly once after a successful mint and disappears the moment the
 * modal closes.
 *
 * Matches the existing kiosk-token UX (see ApiKeysAdminPage) where
 * possible, including the "copy and confirm" pattern for one-time
 * secret reveal.
 */
interface SharePanelProps {
  reportId: string;
  /** Most recent successful HTML run, used as the default snapshot pin. */
  latestSuccessfulHtmlRunId?: string | null;
  onClose(): void;
}

const EXPIRY_OPTIONS: Array<{ label: string; hours: number | null }> = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: '90 days', hours: 24 * 90 },
  { label: 'Never', hours: null },
];

export function SharePanel({
  reportId,
  latestSuccessfulHtmlRunId,
  onClose,
}: SharePanelProps): JSX.Element {
  const sharesQ = useReportShares(reportId);
  const mint = useMintShare(reportId);
  const revoke = useRevokeShare(reportId);

  const [mode, setMode] = useState<ShareMode>('snapshot');
  const [audience, setAudience] = useState<ShareAudience | ''>('');
  const [label, setLabel] = useState('');
  const [expiryHours, setExpiryHours] = useState<number | null>(24 * 7);
  const [justMinted, setJustMinted] = useState<MintShareResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleMint(): Promise<void> {
    setError(null);
    if (mode === 'snapshot' && !latestSuccessfulHtmlRunId) {
      setError(
        'No successful HTML run available to snapshot. Render the report once first, or switch to live mode.',
      );
      return;
    }
    try {
      const trimmedLabel = label.trim();
      const result = await mint.mutateAsync({
        mode,
        ...(mode === 'snapshot' ? { pinnedRunId: latestSuccessfulHtmlRunId } : {}),
        ...(audience ? { audience } : {}),
        ...(trimmedLabel ? { label: trimmedLabel } : {}),
        expiresAt:
          expiryHours === null
            ? null
            : new Date(Date.now() + expiryHours * 3600 * 1000).toISOString(),
      });
      setJustMinted(result);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function handleCopy(url: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setError('Clipboard write blocked by the browser.');
    }
  }

  return (
    <div className="share-modal-backdrop" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <header className="share-modal-header">
          <h2>Share this report</h2>
          <button type="button" className="share-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {justMinted ? (
          <ShareJustMinted
            result={justMinted}
            onCopy={handleCopy}
            copied={copied}
            onDone={() => {
              setJustMinted(null);
              setLabel('');
            }}
          />
        ) : (
          <>
            <section className="share-modal-section">
              <h3 className="share-modal-section-title">New share link</h3>

              <div className="share-form-row">
                <label>
                  <span>Mode</span>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.currentTarget.value as ShareMode)}
                  >
                    <option value="snapshot">Snapshot — pin the most recent run</option>
                    <option value="live">Live — re-render on each load</option>
                  </select>
                </label>
                <p className="text-dim text-xs">
                  {mode === 'snapshot'
                    ? 'Recipients see the exact data from your most recent successful HTML render. Cheap on every load.'
                    : 'Recipients see fresh data on every load. Each visit triggers a new render.'}
                </p>
              </div>

              <div className="share-form-row">
                <label>
                  <span>Audience</span>
                  <select
                    value={audience}
                    onChange={(e) => setAudience(e.currentTarget.value as ShareAudience | '')}
                  >
                    <option value="">Inherit from report</option>
                    <option value="internal">Internal</option>
                    <option value="customer">Customer</option>
                    <option value="partner">Partner</option>
                    <option value="regulator">Regulator</option>
                  </select>
                </label>
                <p className="text-dim text-xs">
                  Overrides which audience-specific overlays (header, footer, watermark) apply for
                  recipients of this link.
                </p>
              </div>

              <div className="share-form-row">
                <label>
                  <span>Expires</span>
                  <select
                    value={String(expiryHours)}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setExpiryHours(v === 'null' ? null : Number(v));
                    }}
                  >
                    {EXPIRY_OPTIONS.map((o) => (
                      <option key={o.label} value={o.hours === null ? 'null' : String(o.hours)}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="share-form-row">
                <label>
                  <span>Label (optional)</span>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.currentTarget.value)}
                    placeholder="e.g. Weekly update for stakeholders"
                    maxLength={120}
                  />
                </label>
              </div>

              {error && <div className="alert alert--danger">{error}</div>}

              <div className="share-form-actions">
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void handleMint()}
                  disabled={mint.isPending}
                >
                  {mint.isPending ? 'Generating…' : 'Generate share link'}
                </button>
              </div>
            </section>

            <ShareList
              shares={sharesQ.data ?? []}
              isLoading={sharesQ.isLoading}
              onRevoke={(id) => {
                if (
                  window.confirm(
                    'Revoke this share link? Anyone using it will lose access immediately.',
                  )
                ) {
                  revoke.mutate(id);
                }
              }}
            />
          </>
        )}

        <SharePanelStyles />
      </div>
    </div>
  );
}

function ShareJustMinted({
  result,
  onCopy,
  copied,
  onDone,
}: {
  result: MintShareResult;
  onCopy(url: string): void;
  copied: boolean;
  onDone(): void;
}): JSX.Element {
  const url = absoluteViewerUrl(result.viewerUrl);
  return (
    <section className="share-modal-section share-just-minted">
      <h3 className="share-modal-section-title">Your share link is ready</h3>
      <p className="text-dim text-sm">
        Copy it now — for security, we won't show the URL again. Anyone with this link can view the
        report until it expires or you revoke it.
      </p>
      <div className="share-token-row">
        <code className="share-token">{url}</code>
        <button type="button" className="btn btn--primary" onClick={() => onCopy(url)}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="share-form-actions">
        <button type="button" className="btn" onClick={onDone}>
          Done
        </button>
      </div>
    </section>
  );
}

function ShareList({
  shares,
  isLoading,
  onRevoke,
}: {
  shares: ReadonlyArray<ReportShare>;
  isLoading: boolean;
  onRevoke(id: string): void;
}): JSX.Element {
  return (
    <section className="share-modal-section">
      <h3 className="share-modal-section-title">Existing shares</h3>
      {isLoading && <div className="text-dim">Loading…</div>}
      {!isLoading && shares.length === 0 && (
        <div className="text-dim text-sm">No share links yet.</div>
      )}
      {shares.length > 0 && (
        <table className="share-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Mode</th>
              <th>Audience</th>
              <th>Expires</th>
              <th>Status</th>
              <th>Used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {shares.map((s) => (
              <tr key={s.id} className={s.revokedAt ? 'share-row--revoked' : ''}>
                <td>{s.label || <span className="text-dim">(no label)</span>}</td>
                <td>{s.mode}</td>
                <td>{s.audience ?? <span className="text-dim">inherit</span>}</td>
                <td>{s.expiresAt ? formatDate(s.expiresAt) : 'Never'}</td>
                <td>
                  {s.revokedAt ? (
                    <span className="badge badge--neutral">Revoked</span>
                  ) : isExpired(s.expiresAt) ? (
                    <span className="badge badge--neutral">Expired</span>
                  ) : (
                    <span className="badge badge--accent">Active</span>
                  )}
                </td>
                <td>
                  {s.accessCount > 0 ? (
                    `${s.accessCount}× · last ${formatRelative(s.lastAccessedAt)}`
                  ) : (
                    <span className="text-dim">never</span>
                  )}
                </td>
                <td>
                  {!s.revokedAt && (
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={() => onRevoke(s.id)}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function isExpired(iso: string | null): boolean {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

function formatDate(iso: string): string {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function SharePanelStyles(): JSX.Element {
  return (
    <style>{`
      .share-modal-backdrop{
        position:fixed;inset:0;background:rgba(15,23,42,0.55);
        display:flex;align-items:center;justify-content:center;z-index:1000;
        padding:var(--space-4);
      }
      .share-modal{
        background:var(--color-surface-1);border-radius:var(--radius-lg);
        max-width:760px;width:100%;max-height:88vh;overflow:auto;
        box-shadow:0 20px 60px rgba(0,0,0,0.25);
      }
      .share-modal-header{
        display:flex;align-items:center;justify-content:space-between;
        padding:var(--space-4) var(--space-5);
        border-bottom:1px solid var(--color-border-subtle);
      }
      .share-modal-header h2{margin:0;font-size:var(--font-size-lg);font-weight:var(--font-weight-semibold)}
      .share-modal-close{
        appearance:none;background:transparent;border:none;cursor:pointer;
        font-size:24px;line-height:1;color:var(--color-text-tertiary);padding:0 6px;
      }
      .share-modal-section{padding:var(--space-4) var(--space-5)}
      .share-modal-section + .share-modal-section{border-top:1px solid var(--color-border-subtle)}
      .share-modal-section-title{
        font-size:var(--font-size-sm);font-weight:var(--font-weight-semibold);
        letter-spacing:0.04em;text-transform:uppercase;color:var(--color-text-tertiary);
        margin:0 0 var(--space-3) 0;
      }
      .share-form-row{margin-bottom:var(--space-3)}
      .share-form-row label{display:flex;flex-direction:column;gap:4px;font-size:var(--font-size-sm);font-weight:var(--font-weight-medium)}
      .share-form-row select,.share-form-row input{
        font:inherit;padding:6px 8px;border:1px solid var(--color-border);
        border-radius:var(--radius-sm);background:var(--color-surface-1);
      }
      .share-form-row p{margin:4px 0 0 0}
      .share-form-actions{display:flex;justify-content:flex-end;gap:var(--space-2);margin-top:var(--space-3)}
      .share-token-row{display:flex;gap:var(--space-2);align-items:center;margin:var(--space-3) 0}
      .share-token{
        flex:1;padding:8px 12px;background:var(--color-surface-2);
        border:1px solid var(--color-border-subtle);border-radius:var(--radius-sm);
        font-family:var(--font-mono);font-size:var(--font-size-sm);
        word-break:break-all;
      }
      .share-table{width:100%;border-collapse:collapse;font-size:var(--font-size-sm)}
      .share-table th,.share-table td{
        text-align:left;padding:6px 8px;border-bottom:1px solid var(--color-border-subtle);
      }
      .share-table th{font-weight:var(--font-weight-semibold);color:var(--color-text-tertiary);
        font-size:var(--font-size-xs);text-transform:uppercase;letter-spacing:0.04em;
      }
      .share-row--revoked td{opacity:0.55}
      .btn{
        font:inherit;font-size:var(--font-size-sm);font-weight:var(--font-weight-semibold);
        padding:6px 14px;border-radius:var(--radius-sm);
        border:1px solid var(--color-border);background:var(--color-surface-1);color:var(--color-text-primary);
        cursor:pointer;
      }
      .btn:hover{background:var(--color-surface-2)}
      .btn--primary{background:var(--color-accent);border-color:var(--color-accent);color:#fff}
      .btn--primary:hover{filter:brightness(1.05)}
      .btn--primary:disabled{opacity:0.55;cursor:default}
      .btn--sm{font-size:var(--font-size-xs);padding:4px 10px}
      .btn--ghost{background:transparent;border-color:transparent;color:var(--color-text-tertiary)}
      .btn--ghost:hover{color:var(--color-danger,#b91c1c);background:var(--color-surface-2)}
    `}</style>
  );
}
